import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Check,
  Clock,
  Database,
  FileText,
  Layers,
  Loader2,
  Scale,
  Search,
  Sparkles,
  Tags,
  Users,
  X,
} from 'lucide-react';
import {
  DocumentListItem,
  EdgeCiteAnalysisStatusResponse,
  EdgeCiteAnalysisStep,
} from '../DocumentAPI';

type PipelineStatus = 'pending' | 'processing' | 'completed' | 'failed' | 'planned';

interface DocumentAIAnalysisModalProps {
  isOpen: boolean;
  document: DocumentListItem | null;
  jobId?: string | null;
  isQueueing?: boolean;
  queueError?: string | null;
  onClose: () => void;
  getStatus: (
    documentId: string,
    options: { versionId?: string; jobId?: string },
  ) => Promise<EdgeCiteAnalysisStatusResponse>;
}

interface PipelineStep {
  key: string;
  label: string;
  description: string;
  status: PipelineStatus;
  progress: number;
  message?: string | null;
  results?: Record<string, any> | null;
  icon: React.ComponentType<{ className?: string }>;
}

interface ResultChip {
  label: string;
  value: React.ReactNode;
}

const STATUS_CLASS: Record<PipelineStatus, string> = {
  pending: 'border-slate-200 bg-slate-50 text-slate-500',
  processing: 'border-blue-200 bg-blue-50 text-blue-700',
  completed: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  failed: 'border-red-200 bg-red-50 text-red-700',
  planned: 'border-slate-200 bg-white text-slate-400',
};

const TASK_ALIASES: Record<string, string> = {
  metadata_extraction: 'metadata',
  classification: 'classification',
  content_analysis: 'content',
  citation_extraction: 'citations',
  vectorization: 'vectorization',
  finalization: 'finalization',
};

const formatTime = (value?: string | null) => {
  if (!value) return '';
  return new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const formatElapsed = (startedAt: number | null, completedAt?: string | null) => {
  if (!startedAt) return '00:00';
  const end = completedAt ? new Date(completedAt).getTime() : Date.now();
  const seconds = Math.max(0, Math.floor((end - startedAt) / 1000));
  const mm = String(Math.floor(seconds / 60)).padStart(2, '0');
  const ss = String(seconds % 60).padStart(2, '0');
  return `${mm}:${ss}`;
};

const normalizeStatus = (status?: string): PipelineStatus => {
  if (status === 'completed') return 'completed';
  if (status === 'processing') return 'processing';
  if (status === 'failed') return 'failed';
  return 'pending';
};

const resultNumber = (value: unknown): number | null => {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
};

const isRecord = (value: unknown): value is Record<string, any> => {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
};

const formatList = (value: unknown): string | null => {
  if (!Array.isArray(value) || value.length === 0) return null;
  return value.map((item) => String(item).replace(/_/g, ' ')).join(', ');
};

const formatConfidence = (value: unknown): string | null => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return `${Math.round(value * 100)}%`;
};

const genericResultChips = (results: Record<string, any>): ResultChip[] => {
  return Object.entries(results)
    .filter(([, value]) => !isRecord(value))
    .slice(0, 4)
    .map(([key, value]) => ({
      label: key.replace(/_/g, ' '),
      value: Array.isArray(value) ? value.length : String(value),
    }));
};

const resultChipsForStep = (step: PipelineStep): ResultChip[] => {
  const results = step.results;
  if (!results) return [];

  if (step.key === 'classification') {
    return [
      { label: 'type', value: String(results.primary_type || 'unknown').replace(/_/g, ' ') },
      results.sub_type ? { label: 'subtype', value: String(results.sub_type).replace(/_/g, ' ') } : null,
      { label: 'confidence', value: formatConfidence(results.confidence) || 'n/a' },
      formatList(results.signals) ? { label: 'signals', value: formatList(results.signals) } : null,
    ].filter(Boolean) as ResultChip[];
  }

  if (step.key === 'clauses') {
    return [
      { label: 'clause signals', value: resultNumber(results.clause_count) ?? 0 },
      formatList(results.clause_types) ? { label: 'signal types', value: formatList(results.clause_types) } : null,
    ].filter(Boolean) as ResultChip[];
  }

  if (step.key === 'parties') {
    return [
      { label: 'role mentions', value: resultNumber(results.party_count) ?? 0 },
      formatList(results.party_roles) ? { label: 'roles detected', value: formatList(results.party_roles) } : null,
      { label: 'unique names', value: 'not extracted' },
    ].filter(Boolean) as ResultChip[];
  }

  return genericResultChips(results);
};

const StepIndicator: React.FC<{ status: PipelineStatus }> = ({ status }) => {
  if (status === 'completed') {
    return (
      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-emerald-600 text-white">
        <Check className="h-4 w-4" />
      </span>
    );
  }
  if (status === 'processing') {
    return (
      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-blue-600 text-white">
        <Loader2 className="h-4 w-4 animate-spin" />
      </span>
    );
  }
  if (status === 'failed') {
    return (
      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-red-600 text-white">
        <AlertTriangle className="h-4 w-4" />
      </span>
    );
  }
  return <span className="h-7 w-7 rounded-full border border-slate-300 bg-white" />;
};

const StatusPill: React.FC<{ status: EdgeCiteAnalysisStatusResponse['status'] | 'queueing' | 'error' }> = ({ status }) => {
  const className = status === 'completed'
    ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
    : status === 'failed' || status === 'error'
      ? 'bg-red-100 text-red-700 border-red-200'
      : status === 'processing'
        ? 'bg-blue-100 text-blue-700 border-blue-200'
        : 'bg-slate-100 text-slate-700 border-slate-200';
  const label = status === 'not_started' ? 'Not Started' : status.charAt(0).toUpperCase() + status.slice(1);
  return <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${className}`}>{label}</span>;
};

export const DocumentAIAnalysisModal: React.FC<DocumentAIAnalysisModalProps> = ({
  isOpen,
  document,
  jobId,
  isQueueing = false,
  queueError,
  onClose,
  getStatus,
}) => {
  const [status, setStatus] = useState<EdgeCiteAnalysisStatusResponse | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [, setTick] = useState(0);

  const versionId = document?.latest_version?.version_id;

  useEffect(() => {
    if (!isOpen) return;
    setStartedAt(Date.now());
    setStatusError(null);
  }, [isOpen, document?.document_id, jobId]);

  useEffect(() => {
    if (!isOpen) return;
    const timer = window.setInterval(() => setTick((tick) => tick + 1), 1000);
    return () => window.clearInterval(timer);
  }, [isOpen]);

  const refreshStatus = useCallback(async () => {
    if (!isOpen || !document?.document_id || queueError) return;
    if (isQueueing && !jobId) return;

    try {
      const nextStatus = await getStatus(document.document_id, {
        versionId,
        jobId: jobId || undefined,
      });
      setStatus(nextStatus);
      setStatusError(null);
    } catch (error) {
      setStatusError(error instanceof Error ? error.message : 'Unable to load analysis status.');
    }
  }, [document?.document_id, getStatus, isOpen, isQueueing, jobId, queueError, versionId]);

  useEffect(() => {
    if (!isOpen) return;
    refreshStatus();
    const timer = window.setInterval(() => {
      if (status?.status === 'completed' || status?.status === 'failed' || queueError) return;
      refreshStatus();
    }, 1500);
    return () => window.clearInterval(timer);
  }, [isOpen, queueError, refreshStatus, status?.status]);

  const stepByAlias = useMemo(() => {
    const map = new Map<string, EdgeCiteAnalysisStep>();
    status?.steps.forEach((step) => {
      const alias = TASK_ALIASES[step.key] || step.key;
      map.set(alias, step);
    });
    return map;
  }, [status?.steps]);

  const pipelineSteps = useMemo<PipelineStep[]>(() => {
    const vectorStep = stepByAlias.get('vectorization');
    const citationStep = stepByAlias.get('citations');
    const contentStep = stepByAlias.get('content');
    const classificationStep = stepByAlias.get('classification');

    const build = (
      key: string,
      label: string,
      description: string,
      icon: PipelineStep['icon'],
      source?: EdgeCiteAnalysisStep,
      fallback: PipelineStatus = 'pending',
    ): PipelineStep => ({
      key,
      label,
      description,
      icon,
      status: source ? normalizeStatus(source.status) : fallback,
      progress: source?.progress || 0,
      message: source?.message,
      results: source?.results,
    });

    const steps = [
      build('metadata', 'Loading document text', 'Read extracted DMS text truth', FileText, stepByAlias.get('metadata')),
      build('classification', 'Classifying document type', 'Identify legal document family', Tags, classificationStep, 'planned'),
      build('chunking', 'Chunking legal sections', 'Create verified legal chunks', Layers, vectorStep),
      build('citations', 'Extracting citations', 'Capture citation keys and provenance', Scale, citationStep),
      build('clauses', 'Finding clause signals', 'Count clause-type signals, not reviewed clause text', Search, contentStep, 'planned'),
      build('parties', 'Detecting party-role mentions', 'Count role-word mentions, not unique party names', Users, contentStep, 'planned'),
      build('weaviate', 'Embedding into Weaviate', 'Write vectors to EdgeLexDocumentChunk', Database, vectorStep),
      build('finalization', 'Publishing AI insight', 'Persist summary and analysis audit', Sparkles, stepByAlias.get('finalization')),
    ];

    if (isQueueing && !status) {
      return steps.map((step, index) => index === 0 ? { ...step, status: 'processing', progress: 5, message: 'Queueing EdgeCite analysis' } : step);
    }

    if (queueError) {
      return steps.map((step, index) => index === 0 ? { ...step, status: 'failed', message: queueError } : step);
    }

    return steps;
  }, [isQueueing, queueError, status, stepByAlias]);

  const insightData = status?.insight?.data || {};
  const write = insightData.write as Record<string, any> | undefined;
  const verification = insightData.verification as Record<string, any> | undefined;
  const completedAt = status?.status === 'completed' ? status.insight?.updated_at || null : null;
  const modalStatus = queueError ? 'error' : isQueueing && !jobId ? 'queueing' : status?.status || 'queued';

  if (!isOpen || !document) return null;

  const completedSteps = pipelineSteps.filter((step) => step.status === 'completed').length;
  const citationCount = resultNumber(insightData.citation_count) ?? resultNumber(stepByAlias.get('citations')?.results?.citation_count);
  const chunkCount = resultNumber(insightData.chunk_count) ?? resultNumber(stepByAlias.get('vectorization')?.results?.chunk_count);
  const verifiedCount = resultNumber(verification?.verified_count) ?? resultNumber(stepByAlias.get('vectorization')?.results?.verified_count);
  const contentResults = stepByAlias.get('content')?.results || {};
  const insightClauses = isRecord(insightData.clauses) ? insightData.clauses : {};
  const insightParties = isRecord(insightData.parties) ? insightData.parties : {};
  const clauseSignalCount = resultNumber(insightClauses.clause_count) ?? resultNumber(contentResults.clause_count);
  const partyRoleMentionCount = resultNumber(insightParties.party_count) ?? resultNumber(contentResults.party_count);

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/45 px-4 py-6">
      <div className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-lg bg-white shadow-2xl">
        <div className="border-b border-slate-200 bg-slate-50 px-6 py-5">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-blue-600 text-white">
                  <Sparkles className="h-5 w-5" />
                </span>
                <StatusPill status={modalStatus} />
                <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-600">
                  <Clock className="h-3.5 w-3.5" />
                  {formatElapsed(startedAt, completedAt)}
                </span>
              </div>
              <h2 className="truncate text-xl font-semibold text-slate-950">{document.title}</h2>
              <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-600">
                <span className="rounded bg-red-50 px-2 py-1 font-semibold text-red-600">PDF</span>
                {document.client_name && <span className="rounded bg-blue-50 px-2 py-1 text-blue-700">Client: {document.client_name}</span>}
                {(document.matter_description || status?.document?.matter_id) && (
                  <span className="rounded bg-emerald-50 px-2 py-1 text-emerald-700">
                    Matter: {document.matter_description || status?.document?.matter_id}
                  </span>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md p-2 text-slate-500 hover:bg-slate-200 hover:text-slate-900"
              aria-label="Close AI analysis"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="mt-5">
            <div className="mb-2 flex items-center justify-between text-xs font-medium text-slate-600">
              <span>{status?.message || (isQueueing ? 'EdgeCite analysis is being queued.' : 'Preparing analysis status.')}</span>
              <span>{Math.max(status?.progress || 0, Math.round((completedSteps / pipelineSteps.length) * 100))}%</span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-slate-200">
              <div
                className="h-full rounded-full bg-blue-600 transition-all duration-500"
                style={{ width: `${Math.max(status?.progress || 0, Math.round((completedSteps / pipelineSteps.length) * 100))}%` }}
              />
            </div>
          </div>
        </div>

        <div className="grid min-h-0 flex-1 grid-cols-1 overflow-hidden lg:grid-cols-[1.4fr_1fr]">
          <div className="min-h-0 overflow-y-auto p-6">
            <div className="space-y-3">
              {pipelineSteps.map((step, index) => {
                const Icon = step.icon;
                return (
                  <div key={`${step.key}-${index}`} className={`rounded-md border p-4 ${STATUS_CLASS[step.status]}`}>
                    <div className="flex items-start gap-3">
                      <StepIndicator status={step.status} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex min-w-0 items-center gap-2">
                            <Icon className="h-4 w-4 shrink-0" />
                            <h3 className="truncate text-sm font-semibold">{step.label}</h3>
                          </div>
                          <span className="text-xs font-semibold uppercase tracking-wide">
                            {step.status === 'planned' ? 'Next' : step.status}
                          </span>
                        </div>
                        <p className="mt-1 text-xs text-slate-600">{step.message || step.description}</p>
                        {resultChipsForStep(step).length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2 text-xs">
                            {resultChipsForStep(step).map((chip) => (
                              <span key={chip.label} className="rounded border border-white/60 bg-white/70 px-2 py-1 text-slate-700">
                                {chip.label}: {chip.value}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <aside className="min-h-0 overflow-y-auto border-t border-slate-200 bg-slate-50 p-6 lg:border-l lg:border-t-0">
            {(queueError || statusError) && (
              <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {queueError || statusError}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <ResultMetric label="Chunks" value={chunkCount ?? 'Pending'} />
              <ResultMetric label="Citations" value={citationCount ?? 'Pending'} />
              <ResultMetric label="Verified" value={verifiedCount ?? 'Pending'} />
              <ResultMetric label="Model" value={write?.embedding_model || 'Pending'} />
              <ResultMetric label="Clause Signals" value={clauseSignalCount ?? 'Pending'} />
              <ResultMetric label="Role Mentions" value={partyRoleMentionCount ?? 'Pending'} />
            </div>

            <div className="mt-5 rounded-md border border-slate-200 bg-white p-4">
              <h3 className="text-sm font-semibold text-slate-950">AI Insight</h3>
              <p className="mt-2 text-sm text-slate-600">
                {status?.insight?.summary_text || 'The analysis summary will appear here as soon as EdgeCite publishes it.'}
              </p>
              {write?.collection_name && (
                <div className="mt-3 rounded bg-slate-50 px-3 py-2 text-xs text-slate-600">
                  Vector store: {write.collection_name}
                </div>
              )}
            </div>

            <div className="mt-5 rounded-md border border-slate-200 bg-white p-4">
              <h3 className="text-sm font-semibold text-slate-950">Live Feed</h3>
              <div className="mt-3 max-h-56 space-y-3 overflow-y-auto">
                {status?.feed?.length ? (
                  status.feed.map((event, index) => (
                    <div key={`${event.timestamp}-${index}`} className="flex gap-3 text-xs">
                      <span className="w-20 shrink-0 font-medium text-slate-500">{formatTime(event.timestamp)}</span>
                      <span className="min-w-0 text-slate-700">{event.label}</span>
                    </div>
                  ))
                ) : (
                  <div className="text-sm text-slate-500">
                    {isQueueing ? 'Queueing analysis...' : 'Waiting for processing events.'}
                  </div>
                )}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

const ResultMetric: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="rounded-md border border-slate-200 bg-white p-3">
    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
    <p className="mt-1 truncate text-sm font-semibold text-slate-950">{value}</p>
  </div>
);

export default DocumentAIAnalysisModal;
